<template>
  <el-dialog title="成本确认"
             :visible.sync="dialogVisible"
             width="50%"
             top="20px"
             custom-class="cost-confirm-dialog"
             :close-on-click-modal="false"
             destroy-on-close
             @closed="progressAfterDialogClosed">
    <div class="cost-confirm-body">
      <div v-if="nodeResult.cost_audit_status === 'audit_reject'"
           class="reject-notice">
        <div class="reject-header">
          <i class="el-icon-close reject-icon"/>
          <span class="reject-title">审批已驳回</span>
          <span class="reject-sub">请根据驳回原因修改后重新提交</span>
          <span class="reject-time">{{ fullNode.project_node.node_finished_time || '' }}</span>
        </div>
        <div class="reject-body">
          <div class="reject-reason-label">
            <i class="el-icon-info-outline"/>
            驳回原因
          </div>
          <div class="reject-reason-content"
               v-html="formatRejectReason(nodeResult.reject_reason)"/>
        </div>
      </div>
      <div class="form-item-inline">
        <el-switch v-model="form.no_need_confirm"/>
        <span class="label-bold">无需成本确认</span>
        <span class="sub-tip">此开关开启后，必须选择一个复用历史项目</span>
      </div>

      <div class="form-section-item">
        <div class="section-title">
          <span class="label-bold">选择复用历史项目</span>
          <span class="sub-tip">选择后成本数据将自动填充，可在其基础上编辑</span>
        </div>
        <remote-select placeholder="请输入项目名称/编号搜索"
                       v-model="form.assign_project_id"
                       :query-request="getBaiscProjectTable"
                       :find-request="getProjectDetail"
                       :query-params="reuseProjectQueryParams"
                       clearable/>
      </div>

      <template v-if="!form.no_need_confirm">
        <!-- 动态分类卡片列表 -->
        <div v-for="(category, cIndex) in costCategoryMap"
             :key="category.name"
             class="category-card"
             :class="`theme-${category.theme}`">
          <div class="card-header">
            <div class="title-wrap">
              <i :class="category.icon"
                 class="header-icon"/>
              <span class="category-name">{{ category.name }}</span>
              <span class="badge">{{ category.items.length }}/10</span>
            </div>
          </div>

          <div class="card-body">
            <!-- 小类列表 -->
            <div v-if="category.items.length > 0"
                 class="item-list">
              <div v-for="(item, iIndex) in category.items"
                   :key="iIndex"
                   class="cost-item-row"
                   :class="{ 'is-abnormal': isItemReject(item), 'is-modified': item._modified_after_audit_reject }">
                <el-tag v-if="item._modified_after_audit_reject"
                        type="warning"
                        size="mini"
                        class="modified-tag">已修改</el-tag>
                <div class="name-input">
                  <el-input v-model="item.cost_name"
                            placeholder="费用条目"/>
                </div>
                <div class="amount-input">
                  <el-input v-model="item.cost_amount"
                            placeholder="金额">
                    <template slot="append">{{ item.cost_unit || '元/家' }}</template>
                  </el-input>
                </div>
                <i class="el-icon-delete delete-btn"
                   title="删除"
                   @click="removeItem(cIndex, iIndex)"/>
              </div>
            </div>

            <!-- 空状态 -->
            <div v-else
                 class="empty-state">
              <p>暂无小类，点击下方按钮添加</p>
            </div>

            <!-- 添加按钮 -->
            <el-button class="add-btn"
                       icon="el-icon-plus"
                       @click="addItem(cIndex)">
              添加小类
            </el-button>
          </div>
        </div>

        <!-- 温馨提示 -->
        <div class="tips-box">
          <i class="el-icon-warning-outline tips-icon"/>
          <div class="tips-content">
            <div class="tips-title">温馨提示</div>
            <div class="tips-desc">
              每个大类最多支持添加10个小类，请确认无误后进行提交，提交后待领导确认通过可进行后续环节
            </div>
          </div>
        </div>

        <!-- 备注 -->
        <div class="remark-section">
          <div class="remark-label">
            备注 <span class="sub-text">（非必填）</span>
          </div>
          <el-input v-model="form.remarks"
                    type="textarea"
                    :rows="3"
                    placeholder="请输入备注信息..."/>
        </div>
      </template>
    </div>

    <!-- 底部按钮 -->
    <div slot="footer"
         class="dialog-footer">
      <el-button @click="dialogVisible = false">取 消</el-button>
      <el-button type="primary"
                 @click="onSubmit">提 交
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import userWorkInfoMixin from '@views/user-workinfo/user-workinfo.mixin.js'
import projectMixins from '@views/project/project.mixins'

export default {
  mixins: [userWorkInfoMixin, projectMixins],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    projectId: {
      type: String,
      default: '0',
    },
    fullNode: {
      type: Object,
      default: () => ({}),
    },
    customerId: {
      type: [String, Number],
      default: '',
    },
  },
  data () {
    return {
      costCategoryMap: [
        {name: '前端成本', theme: 'blue', icon: 'el-icon-monitor', items: []},
        {name: '后端成本', theme: 'green', icon: 'el-icon-data-line', items: []},
        {name: '其他成本', theme: 'orange', icon: 'el-icon-menu', items: []},
      ],
      form: {
        no_need_confirm: false,
        assign_project_id: null,
        remarks: null,
      },
    }
  },
  computed: {
    dialogVisible: {
      get () {
        return this.visible
      },
      set (val) {
        this.$emit('update:visible', val)
      },
    },
    nodeResult () {
      return _.get(this.fullNode, 'project_node.node_result', {}) || {}
    },
    reuseProjectQueryParams () {
      return this.customerId ? {customerid: this.customerId} : {}
    },
  },
  watch: {
    visible: {
      handler (val) {
        if (val) {
          this.initAttr(this.fullNode.project_node || {})
        }
      },
      immediate: true,
    },
    'form.assign_project_id' (newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.loadProjectCost(newVal)
      }
    },
    costCategoryMap: {
      deep: true,
      handler () {
        this.costCategoryMap.forEach((category) => {
          (category.items || []).forEach((item) => {
            item._modified_after_audit_reject = this.itemFingerprint(item) !== item._fp && this.isItemReject(item)
          })
        })
      },
    },
  },
  methods: {
    ...request.map([
      'getBaiscProjectTable',
      'getProjectDetail',
    ]),
    // 格式化驳回原因（支持换行和列表）
    formatRejectReason (reason) {
      if (!reason) return ''
      return reason.replace(/\n/g, '<br>').replace(/^(\d+[.、]\s*)/gm, '<span class="reject-item-num">$1</span>')
    },
    getCostFieldKey (category) {
      const map = {
        '前端成本': 'front_cost_details',
        '后端成本': 'backend_cost_details',
        '其他成本': 'other_cost_details',
      }
      return map[category.name]
    },
    itemFingerprint (item) {
      const clean = {
        cost_name: (item.cost_name || '').toString(),
        cost_amount: (item.cost_amount || '').toString(),
      }
      return JSON.stringify(clean)
    },
    normalizeItems (list) {
      return (Array.isArray(list) ? list : []).map(item => ({
        ...item,
        _modified_after_audit_reject: false,
        _fp: this.itemFingerprint(item),
      }))
    },
    initAttr (projectNode) {
      const nodeResult = _.get(projectNode, 'node_result', {})

      this.costCategoryMap.forEach((category) => {
        const fieldKey = this.getCostFieldKey(category)
        if (fieldKey && Array.isArray(nodeResult[fieldKey])) {
          category.items = this.normalizeItems(_.cloneDeep(nodeResult[fieldKey]))
        } else {
          category.items = []
        }
      })

      const notConfirm = _.get(nodeResult, 'no_need_confirm', false)
      const assignPId = notConfirm ? _.get(nodeResult, 'assign_project_id', null) : null
      this.form = {
        no_need_confirm: notConfirm,
        assign_project_id: assignPId,
        remarks: _.get(nodeResult, 'remarks', '')
      }
    },
    isItemReject (item) {
      return item.item_audit_status === 'reject'
    },
    // 根据复用的历史项目 id，拉取成本数据并覆盖 costCategoryMap 的 items
    async loadProjectCost (projectId) {
      if (!projectId) return
      try {
        const res = await request.common('getProjectCostApi', {id: projectId})
        const nodeResult = _.get(res, 'data.node_result', {})
        this.costCategoryMap.forEach((category) => {
          const fieldKey = this.getCostFieldKey(category)
          if (fieldKey) {
            const list = Array.isArray(nodeResult[fieldKey]) ? nodeResult[fieldKey] : []
            category.items = this.normalizeItems(_.cloneDeep(list))
          }
        })
      } catch (error) {
        this.$message.error(error.message || '获取复用项目成本数据失败')
      }
    },
    // 添加子条目
    addItem (categoryIndex) {
      const category = this.costCategoryMap[categoryIndex]
      if (category.items.length < 10) {
        const item = {cost_name: '', cost_amount: null, cost_unit: '元/家'}
        category.items.push({
          ...item,
          _modified_after_audit_reject: false,
          _fp: this.itemFingerprint(item),
        })
      } else {
        this.$message.warning('每个大类最多支持添加10个小类')
      }
    },
    // 删除子条目
    removeItem (categoryIndex, itemIndex) {
      this.costCategoryMap[categoryIndex].items.splice(itemIndex, 1)
    },
    // 提交数据
    async onSubmit () {
      if (!this.form.no_need_confirm) {
        const totalCount = this.costCategoryMap.reduce((sum, category) => {
          return sum + (category.items || []).length
        }, 0)

        if (totalCount === 0) {
          this.$message.error('需要进行成本确认时,请至少添加一条项目成本下费用小类条目信息')
          return
        }
        for (let cIndex = 0; cIndex < this.costCategoryMap.length; cIndex++) {
          const category = this.costCategoryMap[cIndex]
          const items = category.items || []

          for (let iIndex = 0; iIndex < items.length; iIndex++) {
            const item = items[iIndex]
            const name = (item.cost_name || '').toString().trim()
            const amount = item.cost_amount
            const isEmptyName = !name
            const isEmptyAmount = amount === '' || amount === null || amount === undefined

            if (isEmptyName) {
              this.$message.error(`「${category.name}」第${iIndex + 1}条费用条目的名称不能为空`)
              return
            }
            if (isEmptyAmount) {
              this.$message.error(`「${category.name}」第${iIndex + 1}条费用条目的金额不能为空`)
              return
            }
          }
        }
      }

      const params = {
        id: this.projectId,
        no_need_confirm: this.form.no_need_confirm,
        assign_project_id: this.form.assign_project_id,
        remarks: this.form.remarks,
      }
      this.costCategoryMap.forEach((category) => {
        const fieldKey = this.getCostFieldKey(category)
        if (fieldKey) {
          params[fieldKey] = (category.items || []).map(item => {
            return {
              cost_name: item.cost_name,
              cost_amount: item.cost_amount,
              cost_unit: item.cost_unit,
            }
          })
        }
      })

      try {
        await this.$confirm(
          '提交后数据将进入审批流程，审批期间及通过后均无法再修改或撤销，请确认成本信息填写无误后再提交。',
          '提示',
          {
            confirmButtonText: '确认提交',
            cancelButtonText: '取消',
            type: 'warning',
          }
        )
      } catch (e) {
        // 用户点击取消，直接返回
        return
      }

      await this.withLoading(async () => {
        try {
          const res = await request.common('addProjectCostConfirmApi', params)

          this.progressDialogAfterSubmitSuccess(res.data)
          this.$message.success('成本确认数据保存成功')
        } catch (error) {
          this.$message.error(error.message || '保存失败')
        }
      })
    },
  },
}
</script>

<style lang="scss"
       scoped>
.cost-confirm-body {
  padding: 0 4px;
}

/* 驳回原因提示 */
.reject-notice {
  background-color: #fef2f2;
  border: 1px solid #fbc4c4;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 20px;

  .reject-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }

  .reject-icon {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background-color: #f56c6c;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    flex-shrink: 0;
  }

  .reject-title {
    font-weight: 600;
    color: #f56c6c;
    font-size: 15px;
  }

  .reject-sub {
    color: #e6a23c;
    font-size: 13px;
    flex: 1;
  }

  .reject-time {
    color: #fc2d2d;
    font-size: 12px;
    flex-shrink: 0;
  }

  .reject-body {
    background-color: #fff;
    border-radius: 6px;
    padding: 14px 18px;
  }

  .reject-reason-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    color: #303133;
    font-size: 13px;
    margin-bottom: 5px;

    i {color: #e6a23c;}
  }

  .reject-reason-content {
    color: #606266;
    font-size: 13px;
    line-height: 1.2;

    .reject-item-num {
      color: #f56c6c;
      font-weight: 600;
      margin-right: 4px;
    }
  }
}

.label-bold {
  font-weight: 600;
  color: #303133;
  margin-right: 8px;
}

.sub-tip {
  font-size: 12px;
  color: #909399;
}

.form-item-inline {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  gap: 8px;
}

.form-section-item {
  margin-bottom: 20px;
}

.section-title {
  margin-bottom: 8px;
}

/* 动态分类卡片基类 */
.category-card {
  border: 1px solid #ebedf0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  background-color: #fafafa;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.category-name {
  font-weight: 600;
  font-size: 15px;
  color: #303133;
}

.badge {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
}

/* 主题颜色样式 */
.theme-blue {
  .header-icon {
    color: #2f54eb;
  }

  .badge {
    background-color: #e6f7ff;
    color: #1890ff;
  }

  .add-btn {
    width: 100%;
    border-color: #adc6ff;
    color: #2f54eb;
    background-color: #f0f5ff;

    &:hover {
      background-color: #d6e4ff;
    }
  }
}

.theme-green {
  .header-icon {
    color: #52c41a;
  }

  .badge {
    background-color: #f6ffed;
    color: #52c41a;
  }

  .add-btn {
    width: 100%;
    border-color: #b7eb8f;
    color: #52c41a;
    background-color: #f6ffed;

    &:hover {
      background-color: #d9f7be;
    }
  }
}

.theme-orange {
  .header-icon {
    color: #fa8c16;
  }

  .badge {
    background-color: #fff7e6;
    color: #fa8c16;
  }

  .add-btn {
    width: 100%;
    border-color: #ffd591;
    color: #fa8c16;
    background-color: #fff7e6;

    &:hover {
      background-color: #ffe7ba;
    }
  }
}

/* 条目编辑行 */
.cost-item-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  &.is-abnormal {
    background-color: #fef0f0;
    border: 1px solid #f56c6c;
    border-left: 4px solid #f56c6c;
    border-radius: 4px;
    padding: 6px 10px;
    margin-left: -10px;

    .name-input .el-input__inner,
    .amount-input .el-input__inner {
      border-color: #f56c6c;
      color: #f56c6c;
      background-color: #fff5f5;
      font-weight: 600;
    }
  }

  &.is-modified {
    background-color: #fffbe6;
    border: 1px solid #ffe58f;
    border-left: 4px solid #fa8c16;
    border-radius: 4px;
    padding: 6px 10px;
    margin-left: -10px;

    .name-input .el-input__inner,
    .amount-input .el-input__inner {
      border-color: #ffd591;
      color: #d46b08;
      background-color: #fffdf5;
      font-weight: 600;
    }
  }

  &.is-abnormal.is-modified {
    background-color: #fef0f0;
    border-color: #f56c6c;
    border-left: 4px solid #f56c6c;

    .name-input .el-input__inner,
    .amount-input .el-input__inner {
      border-color: #f56c6c;
      color: #f56c6c;
      background-color: #fff5f5;
    }
  }
}

.modified-tag {
  flex-shrink: 0;
}

.name-input {
  flex: 2;
}

.amount-input {
  flex: 1;
}

.delete-btn {
  font-size: 16px;
  color: #909399;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #f56c6c;
  }
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 16px 0;
  color: #909399;
  font-size: 13px;
}

/* 温馨提示 */
.tips-box {
  background-color: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 6px;
  padding: 12px 16px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 20px;

  .tips-icon {
    color: #fa8c16;
    font-size: 18px;
    margin-top: 2px;
  }

  .tips-title {
    font-weight: 600;
    color: #d46b08;
    font-size: 13px;
    margin-bottom: 2px;
  }

  .tips-desc {
    font-size: 12px;
    color: #d46b08;
  }
}

/* 备注 */
.remark-section {
  margin-bottom: 10px;

  .remark-label {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 8px;
  }

  .sub-text {
    font-weight: normal;
    color: #909399;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/project/components/ProjectCostConfirmDialog.vue